import type { AnyClock } from "@repo/lib/clock";
import { ObjectKeyHasher } from "@repo/lib/keyHasher";
import {
  RepoCache,
  type GetObservableOptions,
} from "@repo/shared-client/repoCache";
import {
  CreateDocumentDtoSchema,
  PatchDocumentDtoSchema,
  type Attachment,
  type AttachmentType,
  type Document,
} from "@repo/types";
import {
  cacheDefaults,
  hookPriorities,
  messageServiceSubscribe,
  SubscriptionMap,
} from "./common";
import { apiService } from "@/services/apiService";
import { nullObservable } from "@repo/shared-client/commonObservables";
import { firstValueFrom, of, type Observable } from "rxjs";
import type { OnlyRequire } from "@repo/lib/types";
import { RepoMutationHooks } from "./repoMutationHooks";
import { toast } from "react-toastify";
import { pickFields } from "@repo/shared/utils";
import { baseRepo } from "./baseRepo";

type DocumentGetKey = { type: "get"; tenantId: string; documentId: string };
type DocumentListKey = { type: "list"; tenantId: string };
type DocumentKey = DocumentGetKey | DocumentListKey;

export type DocumentDto = Document & {
  tenantId: string;
};

export type DocumentIdPath = { tenantId: string; documentId: string | null };

export class DocumentRepository {
  private cache: RepoCache<string, DocumentDto, ObjectKeyHasher, AnyClock>;
  private hooks = new RepoMutationHooks<DocumentIdPath, DocumentDto>();
  private subscribeMap = new SubscriptionMap();

  // expose hook registry
  addHook = this.hooks.addHook;
  removeHook = this.hooks.removeHook;

  publishNewer(document: DocumentDto) {
    this.cache.publish(document.id, document);
  }

  documentToDto(tenantId: string, document: Document): DocumentDto;
  documentToDto(tenantId: string, document?: null): null;
  documentToDto(
    tenantId: string,
    document?: Document | null
  ): DocumentDto | null;
  documentToDto(
    tenantId: string,
    document?: Document | null
  ): DocumentDto | null {
    return document ? { ...document, tenantId } : null;
  }

  constructor() {
    this.cache = new RepoCache({
      requestKeyHasher: new ObjectKeyHasher({ sortKeys: false }),
      expiresAfterSeconds: cacheDefaults.expiresAfterSeconds,
      purgeExpirationAfterSeconds: cacheDefaults.purgeExpiredAfterSeconds,
      fetch: async ({ requestKey, publish }) => {
        const key = requestKey as DocumentKey;

        switch (key.type) {
          case "get": {
            const { tenantId, documentId } = key;
            const document = (
              await apiService.v1.getDocument(tenantId, documentId)
            ).data;

            // only publish if it's the right tenant
            const prev = this.cache.table.get(documentId);
            if (!prev || prev.tenantId === tenantId) {
              publish(documentId, this.documentToDto(tenantId, document));
            }
            break;
          }
          case "list": {
            const { tenantId } = key;
            const documents = (await apiService.v1.listDocuments(tenantId))
              .data;
            for (const document of documents) {
              publish(document.id, this.documentToDto(tenantId, document));
            }
            break;
          }
        }
      },
      collect: ({ requestKey, table }) => {
        const key = requestKey as DocumentKey;

        switch (key.type) {
          case "get": {
            return table.get(key.documentId) ?? null;
          }
          case "list": {
            const documents: DocumentDto[] = [];
            for (const document of table.values()) {
              if (document.tenantId === key.tenantId) {
                documents.push(document);
              }
            }
            return documents;
          }
        }
      },
      affectedKeys: ({ entryData, affectsKey }) => {
        // extract
        const { tenantId, id } = entryData;
        // affect
        affectsKey(this.getKey(tenantId, id));
        affectsKey(this.listKey(tenantId));
      },
      initialSubscribe: ({ strKey, requestKey }) => {
        const key = requestKey as DocumentKey;
        if (key.type !== "get" && key.type !== "list") return;

        const subscription = messageServiceSubscribe(
          "changing.documents",
          key.type === "get" ? [key.documentId] : [],
          () => this.cache.fetch(key)
        );

        this.subscribeMap.addSubscription(strKey, subscription);
      },
      finalUnsubscribe: ({ strKey }) => {
        this.subscribeMap.unsubscribe(strKey);
      },
    });

    baseRepo.addRepo(this);
  }

  private getKey(tenantId: string, documentId: string): DocumentGetKey {
    return { type: "get", tenantId, documentId };
  }
  private listKey(tenantId: string): DocumentListKey {
    return { type: "list", tenantId };
  }

  get(
    tenantId: string,
    documentId: string,
    options?: GetObservableOptions
  ): Observable<DocumentDto | null> {
    if (!tenantId || !documentId) {
      return nullObservable;
    }
    const key = this.getKey(tenantId, documentId);
    return this.cache.observable(key, options);
  }
  getCached(tenantId: string, documentId: string): DocumentDto | null {
    const key = this.getKey(tenantId, documentId);
    return this.cache.collectIfUnexpired(key, null);
  }

  list(
    tenantId: string,
    options?: GetObservableOptions
  ): Observable<DocumentDto[]> {
    if (!tenantId) {
      return of([]);
    }
    const key = this.listKey(tenantId);
    return this.cache.observable(key, options);
  }
  listCached(tenantId: string): DocumentDto[] | null {
    const key = this.listKey(tenantId);
    return this.cache.collectIfUnexpired(key, null);
  }

  async create(
    tenantId: string,
    data: Partial<DocumentDto>
  ): Promise<DocumentDto> {
    if (!tenantId) {
      throw new Error("Failed to create document: tenantId is blank.");
    }

    const hooks = this.hooks.execute({ tenantId, documentId: null }, "create");
    data = await hooks.before(data);

    let created = this.documentToDto(
      tenantId,
      (
        await apiService.v1.createDocument(
          tenantId,
          CreateDocumentDtoSchema.parse(data)
        )
      ).data
    );

    created = await hooks.after(created);

    // publish new document
    this.cache.publish(created.id, created);

    return created;
  }

  async update(
    tenantId: string,
    documentId: string,
    data: Partial<DocumentDto>
  ): Promise<DocumentDto> {
    if (!tenantId || !documentId) {
      throw new Error(
        "Failed to update document: tenantId or documentId is blank."
      );
    }

    const hooks = this.hooks.execute({ tenantId, documentId }, "update");
    data = await hooks.before(data);

    let updated = this.documentToDto(
      tenantId,
      (
        await apiService.v1.updateDocument(
          tenantId,
          documentId,
          PatchDocumentDtoSchema.parse(data)
        )
      ).data
    );

    updated = await hooks.after(updated);

    // publish updated document
    this.cache.publish(updated.id, updated);

    return updated;
  }

  async delete(tenantId: string, documentId: string): Promise<void> {
    if (!tenantId || !documentId) {
      throw new Error(
        "Failed to delete document: tenantId or documentId is blank."
      );
    }

    const hooks = this.hooks.execute({ tenantId, documentId }, "delete");
    await hooks.before(null);

    await apiService.v1.deleteDocument(tenantId, documentId);

    await hooks.after(null);
    this.cache.publish(documentId, null);
  }

  /** Get the attachment that is selected as 'linked'. */
  getLinkedAttachment(
    document: OnlyRequire<DocumentDto, "attachments">
  ): Attachment | null {
    return document.linkedAttachment
      ? document.attachments.find(
          (attachment) => attachment.attachmentId === document.linkedAttachment
        ) ?? null
      : null;
  }

  /** Get the most official attachment. Takes the signed attachment if available. Otherwise
   * falls back to the original. */
  getBestAttachment(
    document: OnlyRequire<DocumentDto, "attachments">
  ): Attachment | null {
    const typeScores: Record<AttachmentType, number> = {
      // 0 means excluded
      editable: 0,
      // ranking
      original: 1,
      signed: 2,
    };
    Object.setPrototypeOf(typeScores, null);

    const bestAttachment = document.attachments.reduce(
      (best, attachment) => {
        if (attachment.isUrl || !attachment.url) return best;
        if (!typeScores[attachment.attachmentType]) return best;

        if (!best) return attachment;

        return typeScores[attachment.attachmentType] >
          typeScores[best.attachmentType]
          ? attachment
          : best;
      },
      null as Attachment | null
    );

    return bestAttachment;
  }

  /** Return the attachment with the given type, or otherwise null. */
  getAttachmentByType(
    document: OnlyRequire<DocumentDto, "attachments">,
    type: AttachmentType
  ): Attachment | null {
    return (
      document.attachments.find(
        (attachment) => attachment.attachmentType === type
      ) ?? null
    );
  }

  /** Replace the attachment of the given type with the new attachment. Returns the  */
  replaceAttachmentByType<
    DocumentType extends OnlyRequire<DocumentDto, "attachments">,
  >(document: DocumentType, attachment: Attachment): DocumentType {
    let index = document.attachments.findIndex(
      (other) => other.attachmentType === attachment.attachmentType
    );
    if (index < 0) index = document.attachments.length;
    document.attachments[index] = attachment;
    return document;
  }

  invalidate(document: OnlyRequire<DocumentDto, "id">) {
    this.cache.invalidateEntry(document.id);
  }

  invalidateAll() {
    this.cache.clear();
  }

  init(data?: Partial<DocumentDto>): DocumentDto {
    return baseRepo.init({
      tenantId: baseRepo.getTenantId() || "UNKNOWN",
      status: "draft",
      name: "",
      description: "",
      attachments: [],
      ...data,
    });
  }
}

export const documentRepo = new DocumentRepository();

const getPrevDocument = async (tenantId: string, documentId: string | null) =>
  documentId
    ? documentRepo.getCached(tenantId, documentId) ??
      (await firstValueFrom(documentRepo.get(tenantId, documentId)))
    : null;

documentRepo.addHook(({ tenantId, documentId }) => {
  const before = async (document: Partial<DocumentDto>) => {
    const prevDocument =
      (await getPrevDocument(tenantId, documentId)) ?? document;

    const mergedDocument = { ...prevDocument, ...document };

    switch (mergedDocument.uploadType) {
      case "meeting-minutes":
        // if minutes are marked official, make them public
        if (mergedDocument.minutesData?.official) {
          document.status = "public";
        }
        break;
      case "meeting-agenda":
        // if agenda document is marked official, make it public
        if (mergedDocument.agendaData?.official) {
          document.status = "public";
        }
        break;
      case "ordinances":
        // if ordinances are marked as passed, make them public
        if (mergedDocument.ordinanceData?.status === "passed") {
          document.status = "public";
        }
        break;
      case "resolutions":
        // if resolutions are marked as passed, make them public
        if (mergedDocument.resolutionData?.status === "passed") {
          document.status = "public";
        }
        break;
    }

    return document;
  };

  return {
    beforeCreate: before,
    beforeUpdate: before,
  };
}, hookPriorities.autoPublish);

const getOriginalAttachmentSignature = (
  doc?: Pick<Document, "attachments">
) => {
  const original = documentRepo.getAttachmentByType(
    { attachments: doc?.attachments ?? [] },
    "original"
  );

  if (!original) return "";

  return JSON.stringify(
    pickFields(original, [
      "attachmentId",
      "extension",
      "isUrl",
      "name",
      "path",
      "sizeBytes",
      "url",
    ])
  );
};

const hasOriginalAttachmentChanged = (
  prevDocument: DocumentDto | undefined,
  nextDocument: Pick<Document, "attachments">
) =>
  getOriginalAttachmentSignature(prevDocument) !==
  getOriginalAttachmentSignature(nextDocument);

documentRepo.addHook(({ tenantId, documentId }) => {
  let shouldWarnAboutOutdatedSigningTemplate = false;

  return {
    async beforeUpdate(document) {
      const prevDocument = await getPrevDocument(tenantId, documentId);

      if (document.attachments && prevDocument) {
        const submissionTemplate =
          document.submissionTemplate === null
            ? null
            : document.submissionTemplate ?? prevDocument.submissionTemplate;

        if (
          submissionTemplate &&
          hasOriginalAttachmentChanged(prevDocument, {
            attachments: document.attachments,
          })
        ) {
          document.submissionTemplate = {
            ...submissionTemplate,
            originalAttachmentOutdated: true,
          };
          shouldWarnAboutOutdatedSigningTemplate = true;
        }
      }

      return document;
    },
    afterUpdate() {
      if (shouldWarnAboutOutdatedSigningTemplate) {
        toast(
          "The original attachment changed. Update the signing template before sending sign requests.",
          { type: "warning" }
        );
      }
    },
  };
}, hookPriorities.detectOutdatedSigningTemplate);

documentRepo.addHook(({ tenantId }) => {
  const after = async (document: DocumentDto) => {
    if (document.minutesData?.official) {
      // push created / updated meeting minutes to Laserfiche
      await apiService.v1.integrationLaserfichePushMeetingMinutes(tenantId, {
        documentId: document.id,
      });
    }
  };

  return {
    afterCreate: after,
    afterUpdate: after,
  };
}, hookPriorities.postSaveActions);
